import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import classNames from "classnames";
import "../Styles/components/AppTopbar.scss";
import { useTranslation } from "react-i18next";
import i18n from "../Config/i18n";
import { Avatar } from "primereact/avatar";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { removeAuthToken } from "../Service/api";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { ProgressSpinner } from 'primereact/progressspinner';
import "../Styles/pages/profile.scss";
import logo from "../assets/images/Reveknew_Logo_All_White.png"
import defaultAvatar from "../../src/assets/images/default avatar.png"
import { useKeycloak } from "@react-keycloak/web";
import DefMembershipActions, { ACTIVE_MEMBERSHIP_STORAGE_KEY } from "../Redux/actions/defMembership-actions";

const isDefaultMembership = (membership) =>
	membership?.isDefault === true || membership?.default === true;

const AppTopbar = ({
	onMenuButtonClick,
	onTopbarMenuButtonClick,
	onTopbarItemClick,
	topbarMenuActive,
	activeTopbarItem,
}) => {
	const onTopbarItemClickIn = (event, item) => {
		if (onTopbarItemClick) {
			onTopbarItemClick({
				originalEvent: event,
				item: item,
			});
		}
	};

	let topbarItemsClassName = classNames("topbar-menu fadeInDown", {
		"topbar-menu-visible": topbarMenuActive,
	});

	const { t } = useTranslation("common");
	const toast = useRef(null);
	const pendingDefaultId = useRef(null);

	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { keycloak } = useKeycloak();

	const defMembership = useSelector((state) => state.defMembership.defMembership);
	const membershipAccounts = useSelector((state) => state.defMembership.membershipAccounts || []);
	const switchingMembership = useSelector((state) => state.defMembership.switching);
	const membershipError = useSelector((state) => state.defMembership.error);
	const profile = useSelector((state) => state.profile.profile);


	const { firstName, lastName } = profile ? profile : ''
	const { email } = profile ? profile : ''
	const { imageUrl } = defMembership ? defMembership?.user : ""
	const { role } = defMembership ? defMembership : { role: "" };
	const { purpose, type, customPmtProvider } = defMembership ? defMembership?.business : ""

	const menuItemsNoColleagues = [
		{
			name: t('nav.user_profile'),
			icon: "pi pi-user",
			onClick: () => {
				navigate("/user/profile")
			},
		},
		{
			name: t('nav.logout'),
			icon: "pi pi-sign-out",
			onClick: () => logoutFn()
		},
	];

	const menuItems = [
		{
			name: t('nav.colleagues'),
			icon: "pi pi-user-plus",
			onClick: () => {
				navigate("teams")
			},
		},
		{
			name: t('settings'),
			icon: "pi pi-cog",
			onClick: () => {
				navigate("settings")
			},
		},
		...(customPmtProvider ? [{
			name: t('nav.payment_providers'),
			icon: "pi pi-credit-card",
			onClick: () => {
				navigate("payment_providers")
			},
		}] : [])
		,
		...menuItemsNoColleagues,
	];

	const profileMenuItems = role === "OWNER" ? menuItems : menuItemsNoColleagues;

	const logoutFn = async () => {

		localStorage.setItem('dialogClosed', JSON.stringify(false));

		removeAuthToken();
		sessionStorage.setItem('crumbs', []);
		try {
			sessionStorage.removeItem(ACTIVE_MEMBERSHIP_STORAGE_KEY);
		} catch (e) {
			// ignore
		}

		const { persistor } = await import("../Redux/store");
		await persistor.purge();

		keycloak.logout({
			redirectUri: process.env.REACT_APP_AUTH_LOGOUTURL,
		});
	};

	const switchMembership = (membershipId) => {
		if (!membershipId || switchingMembership) {
			return;
		}
		if (String(membershipId) === String(defMembership?.id)) {
			return;
		}
		dispatch(DefMembershipActions.switchMembershipRequest(membershipId));
	};

	const setDefaultMembership = (membershipId) => {
		if (!membershipId || switchingMembership) {
			return;
		}
		pendingDefaultId.current = membershipId;
		dispatch(DefMembershipActions.setDefaultMembershipRequest(membershipId));
	};

	useEffect(() => {
		if (switchingMembership || !pendingDefaultId.current) {
			return;
		}

		const requestedId = pendingDefaultId.current;
		pendingDefaultId.current = null;

		if (membershipError) {
			return;
		}

		const updated = membershipAccounts.find(
			(membership) => String(membership.id) === String(requestedId)
		);
		if (!updated || !isDefaultMembership(updated)) {
			return;
		}

		const businessName = updated.business?.name || updated.id;

		toast.current?.show({
			severity: "success",
			summary: t("success"),
			detail: t("nav.default_membership_success", { businessName }),
			life: 3000,
		});
	}, [switchingMembership, membershipAccounts, membershipError, t]);

	return (
		<div className="layout-topbar clearfix" style={{ backgroundColor: "#D0021B" }}>
			<Toast ref={toast} />

			<button className="layout-topbar-logo p-link">
				<img id="layout-topbar-logo" alt="babylon-layout" src={logo} />
			</button>

			<button className="layout-menu-button p-link" onClick={onMenuButtonClick}>
				<i className="pi pi-bars" />
			</button>

			<button
				id="topbar-menu-button"
				className="p-link"
				onClick={onTopbarMenuButtonClick}
			>
				<i className="pi pi-ellipsis-v" />
			</button>

			<ul className={topbarItemsClassName}>
				<li
					className={classNames("user-profile", {
						"active-topmenuitem": activeTopbarItem === "profile",
					})}
					onClick={(e) => onTopbarItemClickIn(e, "profile")}
				>
					<button className="p-link">
						{!defMembership || !profile ?
							<ProgressSpinner style={{ width: '35px', height: '35px', color: 'whitesmoke', marginTop: '0.3rem' }} strokeWidth="8" fill="#666" animationDuration=".8s" />
							:
							<>
								{!imageUrl ? (
									<Avatar
										label={firstName ? firstName[0]?.toUpperCase() : ""}
										shape="circle"
										size="small"
										style={{ backgroundColor: "#666", color: "#ffffff", borderRadius: '30px' }}
									/>
								) : (
									<Avatar
										image={imageUrl}
										size="small"
										shape="circle"
										imageAlt="profile pic"
										style={{ borderRadius: '30px' }}
									/>
								)}
								<span className="topbar-item-name">{t("nav.user_profile")}</span>
							</>
						}
					</button>
					<ul className="fadeInDown user-profile-content">
						{!defMembership || !profile ?
							<div className="grid formgrid">
								<div className="field col-12 md:col-6">
									<Skeleton height="2rem" className="mb-2"></Skeleton>
									<Skeleton className="mb-2"></Skeleton>
									<Skeleton className="mb-2"></Skeleton>
									<Skeleton className="mb-2"></Skeleton>
									<Skeleton className="mb-2"></Skeleton>
									<Skeleton className="mb-2"></Skeleton>
								</div>
							</div>
							:
							<div>
								<ul className="user-profile-items">
									<li>
										<button className="p-link">
											<span>{t("business.organisation_type")}:
												<b className="business-info">
													{t(`business.${type.toLowerCase()}`)}
												</b>
											</span>
										</button>
									</li>
									<li>
										<button className="p-link">
											<span>
												{t("business.account_purpose")}:
												<b className="business-info">
													{t(`business.${purpose.toLowerCase()}`)}
												</b>
											</span>
										</button>
									</li>
								</ul>
								<hr />
								<ul className="user-profile-items">
									{defMembership && (
										<div className="colleague-list-item">
											<div className="surface-card profile p-2">
												<div className="flex surface-border pb-4">
													<img src={imageUrl ? imageUrl : defaultAvatar} className="mr-1" style={{ width: "70px", height: "70px" }} alt="user pic" />
													<div className="flex flex-column align-items-start">
														<span className="user-info">{`${firstName ? firstName : ""} ${lastName ? lastName : ""}`}</span>
														<span className="user-info">{email}</span>
														<span className={`role ${role.toLowerCase()}`}>{role}</span>
													</div>
												</div>
											</div>
										</div>
									)}
								</ul>
								<hr className="topbar-desktop-actions" />
								<div className="topbar-desktop-actions">
									{profileMenuItems.map((list, indx) => (
										<ul className="user-profile-items" key={indx}>
											<li>
												<button className="p-link" onClick={() => list.onClick()}>
													<i className={list.icon} />
													<span>{list.name}</span>
												</button>
											</li>
										</ul>
									))}
								</div>
							</div>
						}
					</ul>
				</li>

				{/* Mobile: Team / Settings / Logout visible in the ellipsis menu */}
				{profileMenuItems.map((list, indx) => (
					<li className="topbar-mobile-actions" key={`mobile-action-${indx}`}>
						<button className="p-link" onClick={() => list.onClick()}>
							<i className={`${list.icon} topbar-icon`} />
							<span className="topbar-item-name">{list.name}</span>
						</button>
					</li>
				))}

				<TranslationMenu
					activeTopbarItem={activeTopbarItem}
					onTopbarItemClick={onTopbarItemClickIn}
					t={t}
				/>
				<Business
					name={defMembership?.business?.name}
					membershipAccounts={membershipAccounts}
					activeMembershipId={defMembership?.id}
					switching={switchingMembership}
					onSwitch={switchMembership}
					onSetDefault={setDefaultMembership}
					activeTopbarItem={activeTopbarItem}
					onTopbarItemClick={onTopbarItemClickIn}
					t={t}
				/>
			</ul>
		</div>
	);
};

AppTopbar.defaultProps = {
	onMenuButtonClick: null,
	onTopbarMenuButtonClick: null,
	onTopbarItemClick: null,
	topbarMenuActive: false,
	activeTopbarItem: null,
	profileMode: null,
	horizontal: null,
};

AppTopbar.prototype = {
	onMenuButtonClick: PropTypes.func.isRequired,
	onTopbarMenuButtonClick: PropTypes.func.isRequired,
	onTopbarItemClick: PropTypes.func.isRequired,
	topbarMenuActive: PropTypes.bool.isRequired,
	activeTopbarItem: PropTypes.string,
	profileMode: PropTypes.string,
	horizontal: PropTypes.bool,
};

const Business = ({
	name,
	membershipAccounts = [],
	activeMembershipId,
	switching,
	onSwitch,
	onSetDefault,
	activeTopbarItem,
	onTopbarItemClick,
	t,
}) => {
	const canSwitch = membershipAccounts.length > 1;

	if (!canSwitch) {
		return (
			<li className="topbar-business">
				<p className="topbar-business-name">{name}</p>
			</li>
		);
	}

	return (
		<li
			className={classNames("topbar-business membership-switcher", {
				"active-topmenuitem": activeTopbarItem === "membership",
			})}
			onClick={(e) => onTopbarItemClick(e, "membership")}
		>
			{/* Do not disable this trigger: disabled buttons swallow clicks (no bubble to li),
			    so a stuck switching flag would permanently prevent the dropdown from opening. */}
			<button className="p-link topbar-business-name" type="button" aria-busy={switching || undefined}>
				<span className="topbar-item-name">{name}</span>
				<span className="topbar-business-label">{name}</span>
				<i className={classNames("pi topbar-business-caret", switching ? "pi-spin pi-spinner" : "pi-angle-down")} />
			</button>
			<ul className="fadeInDown membership-switcher-menu">
				<li className="membership-switcher-heading" onClick={(e) => e.stopPropagation()}>
					<span>{t("nav.switch_membership")}</span>
				</li>
				{membershipAccounts.map((membership) => {
					const isActive = String(membership.id) === String(activeMembershipId);
					const isDefault = isDefaultMembership(membership);
					return (
						<li
							role="menuitem"
							key={membership.id}
							className={classNames("membership-switcher-item", { "is-active": isActive })}
							onClick={(e) => e.stopPropagation()}
						>
							<div className="membership-switcher-details">
								<span className="membership-switcher-business">
									{membership.business?.name || membership.id}
									{isDefault && (
										<i
											className="pi pi-star-fill membership-default-star"
											title={t("nav.default_membership")}
											aria-label={t("nav.default_membership")}
										/>
									)}
								</span>
								<span className="membership-switcher-meta">
									{membership.role}
									{isActive ? ` · ${t("nav.current_membership")}` : ""}
								</span>
							</div>
							{isActive ? (
								<Button
									type="button"
									label={t("nav.set_as_default")}
									icon="pi pi-star"
									className="p-button-sm p-button-outlined membership-set-default-btn"
									loading={switching}
									disabled={switching || isDefault}
									onClick={(e) => {
										e.stopPropagation();
										onSetDefault(membership.id);
									}}
								/>
							) : (
								<Button
									type="button"
									label={t("nav.switch_to_membership")}
									icon="pi pi-arrow-right"
									className="p-button-sm membership-switch-btn"
									loading={switching}
									disabled={switching}
									onClick={(e) => {
										e.stopPropagation();
										onSwitch(membership.id);
									}}
								/>
							)}
						</li>
					);
				})}
			</ul>
		</li>
	);
};

const TranslationMenu = ({ activeTopbarItem, onTopbarItemClick, t }) => {
	const [lang] = useState({
		label: "Language",
		items: [
			{
				label: "English",
				tag: "en",
			},
			{
				label: "French",
				tag: "fr",
			},
		],
	});
	const changeLanguage = (lng) => {
		i18n.changeLanguage(lng);
	};
	return (
		<li
			className={classNames({
				"active-topmenuitem": activeTopbarItem === "language",
			})}
			onClick={(e) => onTopbarItemClick(e, "language")}
		>
			<button className={`lang p-link `}>
				<i className="pi pi-globe p-mt-2 p-ml-2" style={{ 'fontSize': '2em' }} ></i>

				<span className="topbar-item-name">{t(lang.label)}</span>
			</button>
			<ul className="fadeInDown">
				{lang.items.map(({ label, tag }, i) => (
					<li role="menuitem" key={i}>
						<button onClick={() => changeLanguage(tag)} className="p-link">
							<span>{t(label)}</span>
						</button>
					</li>
				))}
			</ul>
		</li>
	);
};
export default AppTopbar;
